import { Button } from '@ksyos/design-system';
import { every, isNull, values } from 'lodash';
import { redenenEcgCustomControlDataSchema } from '@common/validation/customControls/cardiology/holterConsult/cardiology';
import type { ValidationErrors } from '../../../../../utils/validation';
import { emptyValidationErrors, updateValidationErrorsForOne, validate } from '../../../../../utils/validation';
import { InputTriState, NullableInputText } from '../../../../elements/common';
import { Component } from '../../../../elements/Component';
import { FormLabel } from '../../../../elements/forms/FormLabel';
import { HorizontalFormGroup } from '../../../../elements/forms/HorizontalFormGroup';
import { InputContainer } from '../../../../elements/forms/InputContainer';
import { Modal } from '../../../../elements/modals/Modal';
import { AnamneseType } from './AnamneseType';
import { AnamneseWrapper } from './AnamneseWrapper';

interface IRedenenAddEditModalProps {
    anamneseData: IRedenen | null;
    deleteAnamneseData: () => Promise<void>;
    onClose: () => Promise<void>;
    setAnamneseData: (item?: IRedenen) => Promise<void>;
}

interface IRedenenAddEditModalState {
    anamneseData: IRedenen;
    errors: ValidationErrors;
}

export interface IRedenen {
    algemeen_onderzoek: boolean | null;
    atypische_angina_pectoris: boolean | null;
    controle_therapie: boolean | null;
    decompensatio_cordis: boolean | null;
    diabetes_mellitus: boolean | null;
    duizeling_collapsneigingen_wegraking: boolean | null;
    dyspnoe_d_effort: boolean | null;
    familiaal_belast: boolean | null;
    geruststelling: boolean | null;
    hartfalen: boolean | null;
    hartkloppingen_overslag: boolean | null;
    hypercholesterolemie: boolean | null;
    hypertensie: boolean | null;
    kleplijden: boolean | null;
    overig: string | null;
    ritme_geleidingsstoornis: boolean | null;
    typische_angina_pectoris: boolean | null;
    ventrikel_hypertrofie: boolean | null;
    verdenking_ischemie_infarct: boolean | null;
}

export class RedenenAddEditModal extends Component<IRedenenAddEditModalProps, IRedenenAddEditModalState> {
    constructor(props: IRedenenAddEditModalProps) {
        super(props);
        this.state = {
            anamneseData: {
                ritme_geleidingsstoornis: null,
                duizeling_collapsneigingen_wegraking: null,
                hartkloppingen_overslag: null,
                verdenking_ischemie_infarct: null,
                typische_angina_pectoris: null,
                atypische_angina_pectoris: null,
                dyspnoe_d_effort: null,
                hartfalen: null,
                decompensatio_cordis: null,
                ventrikel_hypertrofie: null,
                kleplijden: null,
                diabetes_mellitus: null,
                hypercholesterolemie: null,
                familiaal_belast: null,
                hypertensie: null,
                algemeen_onderzoek: null,
                geruststelling: null,
                controle_therapie: null,
                overig: null,

                ...this.props.anamneseData,
            },
            errors: emptyValidationErrors,
        };
    }

    public render() {
        return (
            <Modal
                actionButtons={[
                    <Button
                        onClick={async () => await this.props.deleteAnamneseData()}
                        variant="outline"
                    >
                        Verwijder
                    </Button>,
                    <Button
                        className="automation-cardioButtonSaveAnamnesis"
                        onClick={async () => await this.validateAnamneseData()}
                    >
                        Anamnese opslaan
                    </Button>,
                ]}
                isOpen
                onClose={async () => await this.props.onClose()}
                size="medium"
                title="Redenen ECG onderzoek"
            >
                <AnamneseWrapper anamneseType={AnamneseType.redenen}>
                    <HorizontalFormGroup hasErrors={this.state.errors.has('ritme_geleidingsstoornis')}>
                        <FormLabel>Ritme / Geleidingsstoornis</FormLabel>
                        <InputContainer>
                            <InputTriState
                                error={this.state.errors.get('ritme_geleidingsstoornis')}
                                name="ritme_geleidingsstoornis"
                                onChange={async (value) => await this.handleChange('ritme_geleidingsstoornis', value)}
                                onDoValidate={async () => await this.validateOne('ritme_geleidingsstoornis')}
                                value={this.state.anamneseData.ritme_geleidingsstoornis}
                            />
                        </InputContainer>
                    </HorizontalFormGroup>

                    <HorizontalFormGroup hasErrors={this.state.errors.has('duizeling_collapsneigingen_wegraking')}>
                        <FormLabel>Duizeling / Collapsneigingen / Wegraking</FormLabel>
                        <InputContainer>
                            <InputTriState
                                error={this.state.errors.get('duizeling_collapsneigingen_wegraking')}
                                name="duizeling_collapsneigingen_wegraking"
                                onChange={async (value) =>
                                    await this.handleChange('duizeling_collapsneigingen_wegraking', value)
                                }
                                onDoValidate={async () =>
                                    await this.validateOne('duizeling_collapsneigingen_wegraking')
                                }
                                value={this.state.anamneseData.duizeling_collapsneigingen_wegraking}
                            />
                        </InputContainer>
                    </HorizontalFormGroup>

                    <HorizontalFormGroup hasErrors={this.state.errors.has('hartkloppingen_overslag')}>
                        <FormLabel>Hartkloppingen / Overslag</FormLabel>
                        <InputContainer>
                            <InputTriState
                                error={this.state.errors.get('hartkloppingen_overslag')}
                                name="hartkloppingen_overslag"
                                onChange={async (value) => await this.handleChange('hartkloppingen_overslag', value)}
                                onDoValidate={async () => await this.validateOne('hartkloppingen_overslag')}
                                value={this.state.anamneseData.hartkloppingen_overslag}
                            />
                        </InputContainer>
                    </HorizontalFormGroup>

                    <HorizontalFormGroup hasErrors={this.state.errors.has('verdenking_ischemie_infarct')}>
                        <FormLabel>Verdenking Ischemie / Infarct</FormLabel>
                        <InputContainer>
                            <InputTriState
                                error={this.state.errors.get('verdenking_ischemie_infarct')}
                                name="verdenking_ischemie_infarct"
                                onChange={async (value) =>
                                    await this.handleChange('verdenking_ischemie_infarct', value)
                                }
                                onDoValidate={async () => await this.validateOne('verdenking_ischemie_infarct')}
                                value={this.state.anamneseData.verdenking_ischemie_infarct}
                            />
                        </InputContainer>
                    </HorizontalFormGroup>

                    <HorizontalFormGroup hasErrors={this.state.errors.has('typische_angina_pectoris')}>
                        <FormLabel>Typische angina pectoris</FormLabel>
                        <InputContainer>
                            <InputTriState
                                error={this.state.errors.get('typische_angina_pectoris')}
                                name="typische_angina_pectoris"
                                onChange={async (value) => await this.handleChange('typische_angina_pectoris', value)}
                                onDoValidate={async () => await this.validateOne('typische_angina_pectoris')}
                                value={this.state.anamneseData.typische_angina_pectoris}
                            />
                        </InputContainer>
                    </HorizontalFormGroup>

                    <HorizontalFormGroup hasErrors={this.state.errors.has('atypische_angina_pectoris')}>
                        <FormLabel>Atypische angina pectoris</FormLabel>
                        <InputContainer>
                            <InputTriState
                                error={this.state.errors.get('atypische_angina_pectoris')}
                                name="atypische_angina_pectoris"
                                onChange={async (value) => await this.handleChange('atypische_angina_pectoris', value)}
                                onDoValidate={async () => await this.validateOne('atypische_angina_pectoris')}
                                value={this.state.anamneseData.atypische_angina_pectoris}
                            />
                        </InputContainer>
                    </HorizontalFormGroup>

                    <HorizontalFormGroup hasErrors={this.state.errors.has('dyspnoe_d_effort')}>
                        <FormLabel>Dyspnoe d'effort</FormLabel>
                        <InputContainer>
                            <InputTriState
                                error={this.state.errors.get('dyspnoe_d_effort')}
                                name="dyspnoe_d_effort"
                                onChange={async (value) => await this.handleChange('dyspnoe_d_effort', value)}
                                onDoValidate={async () => await this.validateOne('dyspnoe_d_effort')}
                                value={this.state.anamneseData.dyspnoe_d_effort}
                            />
                        </InputContainer>
                    </HorizontalFormGroup>

                    <HorizontalFormGroup hasErrors={this.state.errors.has('hartfalen')}>
                        <FormLabel>Hartfalen</FormLabel>
                        <InputContainer>
                            <InputTriState
                                error={this.state.errors.get('hartfalen')}
                                name="hartfalen"
                                onChange={async (value) => await this.handleChange('hartfalen', value)}
                                onDoValidate={async () => await this.validateOne('hartfalen')}
                                value={this.state.anamneseData.hartfalen}
                            />
                        </InputContainer>
                    </HorizontalFormGroup>

                    <HorizontalFormGroup hasErrors={this.state.errors.has('decompensatio_cordis')}>
                        <FormLabel>Decompensatio cordis</FormLabel>
                        <InputContainer>
                            <InputTriState
                                error={this.state.errors.get('decompensatio_cordis')}
                                name="decompensatio_cordis"
                                onChange={async (value) => await this.handleChange('decompensatio_cordis', value)}
                                onDoValidate={async () => await this.validateOne('decompensatio_cordis')}
                                value={this.state.anamneseData.decompensatio_cordis}
                            />
                        </InputContainer>
                    </HorizontalFormGroup>

                    <HorizontalFormGroup hasErrors={this.state.errors.has('ventrikel_hypertrofie')}>
                        <FormLabel>Ventrikel hypertrofie</FormLabel>
                        <InputContainer>
                            <InputTriState
                                error={this.state.errors.get('ventrikel_hypertrofie')}
                                name="ventrikel_hypertrofie"
                                onChange={async (value) => await this.handleChange('ventrikel_hypertrofie', value)}
                                onDoValidate={async () => await this.validateOne('ventrikel_hypertrofie')}
                                value={this.state.anamneseData.ventrikel_hypertrofie}
                            />
                        </InputContainer>
                    </HorizontalFormGroup>

                    <HorizontalFormGroup hasErrors={this.state.errors.has('kleplijden')}>
                        <FormLabel>Kleplijden</FormLabel>
                        <InputContainer>
                            <InputTriState
                                error={this.state.errors.get('kleplijden')}
                                name="kleplijden"
                                onChange={async (value) => await this.handleChange('kleplijden', value)}
                                onDoValidate={async () => await this.validateOne('kleplijden')}
                                value={this.state.anamneseData.kleplijden}
                            />
                        </InputContainer>
                    </HorizontalFormGroup>

                    <HorizontalFormGroup hasErrors={this.state.errors.has('diabetes_mellitus')}>
                        <FormLabel>Diabetes mellitus</FormLabel>
                        <InputContainer>
                            <InputTriState
                                error={this.state.errors.get('diabetes_mellitus')}
                                name="diabetes_mellitus"
                                onChange={async (value) => await this.handleChange('diabetes_mellitus', value)}
                                onDoValidate={async () => await this.validateOne('diabetes_mellitus')}
                                value={this.state.anamneseData.diabetes_mellitus}
                            />
                        </InputContainer>
                    </HorizontalFormGroup>

                    <HorizontalFormGroup hasErrors={this.state.errors.has('hypercholesterolemie')}>
                        <FormLabel>Hypercholesterolemie</FormLabel>
                        <InputContainer>
                            <InputTriState
                                error={this.state.errors.get('hypercholesterolemie')}
                                name="hypercholesterolemie"
                                onChange={async (value) => await this.handleChange('hypercholesterolemie', value)}
                                onDoValidate={async () => await this.validateOne('hypercholesterolemie')}
                                value={this.state.anamneseData.hypercholesterolemie}
                            />
                        </InputContainer>
                    </HorizontalFormGroup>

                    <HorizontalFormGroup hasErrors={this.state.errors.has('familiaal_belast')}>
                        <FormLabel>Familiaal belast</FormLabel>
                        <InputContainer>
                            <InputTriState
                                error={this.state.errors.get('familiaal_belast')}
                                name="familiaal_belast"
                                onChange={async (value) => await this.handleChange('familiaal_belast', value)}
                                onDoValidate={async () => await this.validateOne('familiaal_belast')}
                                value={this.state.anamneseData.familiaal_belast}
                            />
                        </InputContainer>
                    </HorizontalFormGroup>

                    <HorizontalFormGroup hasErrors={this.state.errors.has('hypertensie')}>
                        <FormLabel>Hypertensie</FormLabel>
                        <InputContainer>
                            <InputTriState
                                error={this.state.errors.get('hypertensie')}
                                name="hypertensie"
                                onChange={async (value) => await this.handleChange('hypertensie', value)}
                                onDoValidate={async () => await this.validateOne('hypertensie')}
                                value={this.state.anamneseData.hypertensie}
                            />
                        </InputContainer>
                    </HorizontalFormGroup>

                    <HorizontalFormGroup hasErrors={this.state.errors.has('algemeen_onderzoek')}>
                        <FormLabel>Algemeen onderzoek</FormLabel>
                        <InputContainer>
                            <InputTriState
                                error={this.state.errors.get('algemeen_onderzoek')}
                                name="algemeen_onderzoek"
                                onChange={async (value) => await this.handleChange('algemeen_onderzoek', value)}
                                onDoValidate={async () => await this.validateOne('algemeen_onderzoek')}
                                value={this.state.anamneseData.algemeen_onderzoek}
                            />
                        </InputContainer>
                    </HorizontalFormGroup>

                    <HorizontalFormGroup hasErrors={this.state.errors.has('geruststelling')}>
                        <FormLabel>Geruststelling</FormLabel>
                        <InputContainer>
                            <InputTriState
                                error={this.state.errors.get('geruststelling')}
                                name="geruststelling"
                                onChange={async (value) => await this.handleChange('geruststelling', value)}
                                onDoValidate={async () => await this.validateOne('geruststelling')}
                                value={this.state.anamneseData.geruststelling}
                            />
                        </InputContainer>
                    </HorizontalFormGroup>

                    <HorizontalFormGroup hasErrors={this.state.errors.has('controle_therapie')}>
                        <FormLabel>Controle therapie</FormLabel>
                        <InputContainer>
                            <InputTriState
                                error={this.state.errors.get('controle_therapie')}
                                name="controle_therapie"
                                onChange={async (value) => await this.handleChange('controle_therapie', value)}
                                onDoValidate={async () => await this.validateOne('controle_therapie')}
                                value={this.state.anamneseData.controle_therapie}
                            />
                        </InputContainer>
                    </HorizontalFormGroup>

                    <HorizontalFormGroup hasErrors={this.state.errors.has('overig')}>
                        <FormLabel>Overig</FormLabel>
                        <InputContainer>
                            <NullableInputText
                                error={this.state.errors.get('overig')}
                                onChange={async (value) => await this.handleChange('overig', value)}
                                onDoValidate={async () => await this.validateOne('overig')}
                                value={this.state.anamneseData.overig}
                            />
                        </InputContainer>
                    </HorizontalFormGroup>
                </AnamneseWrapper>
            </Modal>
        );
    }

    private async handleChange<K extends keyof IRedenen>(name: K, value: IRedenen[K]) {
        await this.setState(({ anamneseData }) => ({ anamneseData: { ...anamneseData, [name]: value } }));
    }

    private async validateOne(name: keyof IRedenen) {
        await this.setState(({ anamneseData, errors }) => ({
            errors: updateValidationErrorsForOne(redenenEcgCustomControlDataSchema, anamneseData, errors, name),
        }));
    }

    private async validateAnamneseData() {
        if (every(values(this.state.anamneseData), isNull)) {
            return;
        }

        const { hasErrors, errors, item } = validate(redenenEcgCustomControlDataSchema, this.state.anamneseData);
        await this.setState({ errors });
        if (!hasErrors) {
            await this.props.setAnamneseData(item);
        }
    }
}
